import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Slider,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandLess,
  ExpandMore,
  Save as SaveIcon,
  VisibilityOff as VisibilityOffIcon,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
} from '@mui/icons-material';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import { useMuiSwitchableTheme, useToastr } from '@farmshare/ui-components';
import { buildCutsheetPreviewData } from 'pages/processor/capabilities/_utils/build-cutsheet-preview-data';
import { downloadFromBase64Encoded } from 'lib/downloadFromBase64Encoded';
import { downloadFromPresignedUrl } from 'lib/downloadFromPresignedUrl';
import {
  useCutsheetCapabilityGenerateBlankCutsheetMutation,
  useCutsheetCapabilityPrintSettingsUpdateMutation,
} from 'lib/graphql';
import { vendorState } from 'state';

/**
 * Full shape of PDF print/layout settings. Mirrors the backend PrintSettings
 * sub-document on `CutsheetCapability` and `CutsheetQuestionnaire`.
 */
export interface PrintSettings {
  baseFontSize: number;
  primalColumns: number;
  specsLayoutPosition: 'top' | 'bottom' | 'left' | 'right';
  checkboxStyle: 'check' | 'cross' | 'fill';
  showPrimalGroup: boolean;
  alwaysShowPrimalNotes: boolean;
  hideUnselectedCuts: boolean;
  orientation: 'portrait' | 'landscape';
  hiddenGroupIds: string[];
  hiddenItemIds: string[];
  hiddenSpecificationIds: string[];
}

const DEFAULT_SETTINGS: PrintSettings = {
  baseFontSize: 15,
  primalColumns: 2,
  specsLayoutPosition: 'right',
  checkboxStyle: 'fill',
  showPrimalGroup: true,
  alwaysShowPrimalNotes: true,
  hideUnselectedCuts: false,
  orientation: 'portrait',
  hiddenGroupIds: [],
  hiddenItemIds: [],
  hiddenSpecificationIds: [],
};

function resolveSettings(saved?: Partial<PrintSettings> | null): PrintSettings {
  return {
    ...DEFAULT_SETTINGS,
    ...(saved ?? {}),
    hiddenGroupIds: saved?.hiddenGroupIds ?? [],
    hiddenItemIds: saved?.hiddenItemIds ?? [],
    hiddenSpecificationIds: saved?.hiddenSpecificationIds ?? [],
  };
}

/** Item IDs considered "attached" to a parent context (e.g. a questionnaire). */
export interface RestrictedIds {
  cutIds: string[];
  specificationIds: string[];
}

interface CutsheetPdfGeneratorProps {
  /** Form values used to build the preview data (primals, cuts, specs, etc.) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formValues: any;
  /** The full capability object; print settings are read from capability.printSettings */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  capability: any;

  /**
   * Controlled settings. When provided alongside `onPrintSettingsChange`, the
   * component is fully controlled and renders from these values.
   */
  printSettings?: Partial<PrintSettings> | null;
  /** Change handler invoked whenever any setting updates in controlled mode. */
  onPrintSettingsChange?: (next: PrintSettings) => void;

  /**
   * Baseline settings to compare against. When provided, any setting that
   * differs from the base is marked as "Overridden" with a reset affordance.
   */
  baseSettings?: Partial<PrintSettings> | null;

  /**
   * Optional restriction set. When provided, a button appears at the top of
   * the Visibility tab that adds every cut/group/spec NOT in `restrictedIds`
   * to the corresponding hidden-ids list.
   */
  restrictedIds?: RestrictedIds | null;
  /** Label for the "hide non-attached" action button. */
  restrictedActionLabel?: string;

  /** Hide the Save button (e.g. when the parent handles saving via a form). */
  hideSaveButton?: boolean;
  /** Hide the "Download Blank" button. */
  hideBlankDownload?: boolean;
}

export function CutsheetPdfGenerator({
  formValues,
  capability,
  printSettings: printSettingsProp,
  onPrintSettingsChange,
  baseSettings,
  restrictedIds,
  restrictedActionLabel = 'Hide items not in this list',
  hideSaveButton,
  hideBlankDownload,
}: CutsheetPdfGeneratorProps) {
  const isControlled = Boolean(onPrintSettingsChange);

  // Uncontrolled mode: initialise from the capability's saved settings.
  const [localSettings, setLocalSettings] = useState<PrintSettings>(() =>
    resolveSettings(capability?.printSettings),
  );

  const settings: PrintSettings = useMemo(
    () => (isControlled ? resolveSettings(printSettingsProp) : localSettings),
    [isControlled, printSettingsProp, localSettings],
  );

  const updateSettings = useCallback(
    (patch: Partial<PrintSettings>) => {
      if (isControlled) {
        onPrintSettingsChange!({ ...settings, ...patch });
      } else {
        setLocalSettings((prev) => ({ ...prev, ...patch }));
      }
    },
    [isControlled, onPrintSettingsChange, settings],
  );

  const resolvedBase = useMemo(
    () => (baseSettings ? resolveSettings(baseSettings) : null),
    [baseSettings],
  );

  const savedSettings = useMemo(
    () => resolveSettings(capability?.printSettings),
    [capability?.printSettings],
  );

  const [renderedHtml, setRenderedHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sidebarTab, setSidebarTab] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(0.82);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { marginTop } = useMuiSwitchableTheme();
  const vendor = useRecoilValue(vendorState);

  const [updatePrintSettings, { loading: isSaving }] =
    useCutsheetCapabilityPrintSettingsUpdateMutation();

  const [generateBlank, { loading: isDownloadingBlank }] =
    useCutsheetCapabilityGenerateBlankCutsheetMutation();

  const { push } = useToastr();

  // Track which groups are expanded in the visibility tree
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  /**
   * Flat list of primals (as "groups") with their cuts, derived from formValues.
   */
  const visibilityTree = useMemo(() => {
    const groups: {
      id: string;
      name: string;
      cuts: { id: string; name: string }[];
    }[] = [];

    for (const group of formValues?.primalGroups ?? []) {
      if (group.isDeleted) continue;
      for (const primal of group.primals ?? []) {
        if (!primal || primal.isDeleted) continue;
        const cuts = (primal.cuts ?? [])
          .filter((c: any) => !c.isDeleted)
          .map((c: any) => ({
            id: c._id ?? c.id,
            name: c.printedText || c.name,
          }));
        if (cuts.length > 0) {
          groups.push({
            id: primal._id ?? group._id ?? group.id,
            name: primal.name || group.name || 'Unnamed',
            cuts,
          });
        }
      }
    }
    return groups;
  }, [formValues]);

  /**
   * Flat list of specifications derived from formValues.
   */
  const specificationsList = useMemo(() => {
    return ((formValues?.specifications ?? []) as any[])
      .filter((s) => s && s._id)
      .map((s) => ({
        id: String(s._id),
        name: s.name || 'Unnamed Specification',
      }));
  }, [formValues]);

  /**
   * Adds every cut / group / specification NOT present in `restrictedIds` to
   * the corresponding hidden-ids list. Existing hidden IDs are preserved —
   * this is additive, so manual tweaks the user made before clicking aren't
   * lost. Groups become hidden only when none of their cuts are attached.
   */
  const handleHideNonAttached = useCallback(() => {
    if (!restrictedIds) return;
    const attachedCuts = new Set(restrictedIds.cutIds);
    const attachedSpecs = new Set(restrictedIds.specificationIds);

    const nextHiddenGroups = new Set(settings.hiddenGroupIds);
    const nextHiddenItems = new Set(settings.hiddenItemIds);
    const nextHiddenSpecs = new Set(settings.hiddenSpecificationIds);

    for (const group of visibilityTree) {
      const anyAttached = group.cuts.some((c) => attachedCuts.has(c.id));
      if (!anyAttached) {
        nextHiddenGroups.add(group.id);
        continue;
      }
      for (const cut of group.cuts) {
        if (!attachedCuts.has(cut.id)) nextHiddenItems.add(cut.id);
      }
    }
    for (const spec of specificationsList) {
      if (!attachedSpecs.has(spec.id)) nextHiddenSpecs.add(spec.id);
    }

    updateSettings({
      hiddenGroupIds: Array.from(nextHiddenGroups),
      hiddenItemIds: Array.from(nextHiddenItems),
      hiddenSpecificationIds: Array.from(nextHiddenSpecs),
    });
  }, [
    restrictedIds,
    settings.hiddenGroupIds,
    settings.hiddenItemIds,
    settings.hiddenSpecificationIds,
    visibilityTree,
    specificationsList,
    updateSettings,
  ]);

  const toggleGroupExpanded = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const toggleGroupHidden = useCallback(
    (groupId: string) => {
      const current = settings.hiddenGroupIds;
      updateSettings({
        hiddenGroupIds: current.includes(groupId)
          ? current.filter((id) => id !== groupId)
          : [...current, groupId],
      });
    },
    [settings.hiddenGroupIds, updateSettings],
  );

  const toggleItemHidden = useCallback(
    (itemId: string) => {
      const current = settings.hiddenItemIds;
      updateSettings({
        hiddenItemIds: current.includes(itemId)
          ? current.filter((id) => id !== itemId)
          : [...current, itemId],
      });
    },
    [settings.hiddenItemIds, updateSettings],
  );

  const toggleSpecificationHidden = useCallback(
    (specId: string) => {
      const current = settings.hiddenSpecificationIds;
      updateSettings({
        hiddenSpecificationIds: current.includes(specId)
          ? current.filter((id) => id !== specId)
          : [...current, specId],
      });
    },
    [settings.hiddenSpecificationIds, updateSettings],
  );

  const hiddenCount =
    settings.hiddenGroupIds.length +
    settings.hiddenItemIds.length +
    settings.hiddenSpecificationIds.length;

  const isDirty = useMemo(() => {
    const keys = Object.keys(DEFAULT_SETTINGS) as (keyof PrintSettings)[];
    return keys.some((key) => {
      const a = settings[key];
      const b = savedSettings[key];
      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return true;
        const bSet = new Set(b);
        return a.some((v) => !bSet.has(v));
      }
      return a !== b;
    });
  }, [settings, savedSettings]);

  // Rebuild template data, filtering out hidden groups/items before the builder
  // runs (since it strips cut IDs during transformation).
  const templateData = useMemo(() => {
    let values = formValues;
    const hiddenGroups = new Set(settings.hiddenGroupIds);
    const hiddenItems = new Set(settings.hiddenItemIds);
    const hiddenSpecs = new Set(settings.hiddenSpecificationIds);
    if (hiddenGroups.size > 0 || hiddenItems.size > 0 || hiddenSpecs.size > 0) {
      values = {
        ...formValues,
        primalGroups: (formValues?.primalGroups ?? []).map((group: any) => ({
          ...group,
          primals: (group.primals ?? []).map((primal: any) => {
            if (!primal) return primal;
            if (hiddenGroups.has(primal._id)) {
              return { ...primal, isDeleted: true };
            }
            if (hiddenItems.size === 0) return primal;
            return {
              ...primal,
              cuts: (primal.cuts ?? []).map((cut: any) =>
                hiddenItems.has(cut?._id) ? { ...cut, isDeleted: true } : cut,
              ),
            };
          }),
        })),
        specifications:
          hiddenSpecs.size > 0
            ? (formValues?.specifications ?? []).filter(
                (s: any) => !hiddenSpecs.has(String(s?._id)),
              )
            : formValues?.specifications,
      };
    }
    return buildCutsheetPreviewData(
      values,
      capability,
      settings.hideUnselectedCuts,
    );
  }, [
    formValues,
    capability,
    settings.hideUnselectedCuts,
    settings.hiddenGroupIds,
    settings.hiddenItemIds,
    settings.hiddenSpecificationIds,
  ]);

  // Update iframe content when renderedHtml changes
  useEffect(() => {
    if (iframeRef.current && renderedHtml) {
      const iframeDoc = iframeRef.current.contentDocument;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(renderedHtml);
        iframeDoc.close();
      }
    }
  }, [renderedHtml]);

  // Fetch rendered HTML from server whenever settings change
  useEffect(() => {
    const fetchRenderedHtml = async () => {
      setIsLoading(true);
      setRenderError(null);

      try {
        const data = {
          ...templateData,
          vendor: {
            ...templateData.vendor,
            name: vendor?.shop_name ?? templateData.vendor.name,
            logo_url:
              vendor?.tenant?.picture_url ?? templateData.vendor.logo_url,
          },
          base_font_size: settings.baseFontSize,
          primal_columns: settings.primalColumns,
          specs_layout_position: settings.specsLayoutPosition,
          checkbox_style: settings.checkboxStyle,
          show_primal_group: settings.showPrimalGroup,
          always_show_primal_notes: settings.alwaysShowPrimalNotes,
          hide_unselected_cuts: settings.hideUnselectedCuts,
          orientation: settings.orientation,
        };

        const response = await fetch(
          `${import.meta.env?.VITE_GRAPHQL_URL || '/'}api/cutsheet-preview/render-cutsheet-preview`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to render template');
        }

        const html = await response.text();
        setRenderedHtml(html);
      } catch (err) {
        const fetchErr = err as Error;
        setRenderError(fetchErr.message);
        setRenderedHtml(
          `<div style="color: red; padding: 20px;">Error rendering template: ${fetchErr.message}</div>`,
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchRenderedHtml();
  }, [templateData, settings, vendor]);

  /**
   * Calls the API to generate a blank (no selections) cutsheet PDF from the
   * capability's current catalog and downloads it to the user's machine.
   */
  const handleDownloadBlank = useCallback(async () => {
    if (!capability?._id) return;
    try {
      const response = await generateBlank({
        variables: { cutsheetCapabilityId: capability._id },
      });

      const result = response.data?.cutsheetCapabilityGenerateBlankCutsheet;

      if (result?.fileUrl) {
        downloadFromPresignedUrl(result.fileUrl);
      } else if (result?.file) {
        downloadFromBase64Encoded(
          result.file,
          result.filename ?? 'Blank_Cutsheet.pdf',
        );
      }
    } catch (error) {
      push({
        title: 'Error',
        body: (error as Error).message ?? 'Failed to generate blank cutsheet.',
        bg: 'danger',
        delay: 4000,
      });
    }
  }, [capability, generateBlank, push]);

  /**
   * Persists the current design settings back to the capability so that every
   * future print job starts from the same layout without re-adjusting controls.
   */
  const handleSaveSettings = async () => {
    if (!capability?._id) return;
    await updatePrintSettings({
      variables: {
        id: capability._id,
        baseFontSize: settings.baseFontSize,
        primalColumns: settings.primalColumns,
        specsLayoutPosition: settings.specsLayoutPosition,
        checkboxStyle: settings.checkboxStyle,
        showPrimalGroup: settings.showPrimalGroup,
        alwaysShowPrimalNotes: settings.alwaysShowPrimalNotes,
        hideUnselectedCuts: settings.hideUnselectedCuts,
        orientation: settings.orientation,
        hiddenGroupIds: settings.hiddenGroupIds,
        hiddenItemIds: settings.hiddenItemIds,
        hiddenSpecificationIds: settings.hiddenSpecificationIds,
      },
    });
    setSaveSuccess(true);
  };

  /** True when the setting differs from the provided base. */
  const isOverridden = (key: keyof PrintSettings): boolean => {
    if (!resolvedBase) return false;
    const a = settings[key];
    const b = resolvedBase[key];
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return true;
      const bSet = new Set(b);
      return a.some((v) => !bSet.has(v));
    }
    return a !== b;
  };

  const resetSetting = (key: keyof PrintSettings) => {
    if (!resolvedBase) return;
    updateSettings({ [key]: resolvedBase[key] } as Partial<PrintSettings>);
  };

  /** Label + optional override chip/reset, rendered above a control. */
  const SettingRow: React.FC<{
    title: ReactNode;
    description?: ReactNode;
    settingKey: keyof PrintSettings;
    children: ReactNode;
  }> = ({ title, description, settingKey, children }) => {
    const overridden = isOverridden(settingKey);
    return (
      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 0.25 }}
        >
          <Typography variant="body2" fontWeight="medium">
            {title}
          </Typography>
          {overridden && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip
                label="Overridden"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
              />
              <Link
                component="button"
                type="button"
                underline="hover"
                variant="caption"
                onClick={() => resetSetting(settingKey)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Reset
              </Link>
            </Stack>
          )}
        </Stack>
        {description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1, display: 'block' }}
          >
            {description}
          </Typography>
        )}
        {children}
      </Box>
    );
  };

  const paperWidth = settings.orientation === 'landscape' ? 1100 : 850;
  const paperHeight = settings.orientation === 'landscape' ? 850 : 1100;

  // ── Shared preview block (rendered once, shown in sidebar or standalone) ───
  const previewContent = (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: '#525659',
        borderRadius: 1,
        minHeight: isMobile ? 'calc(100vh - 120px)' : undefined,
      }}
    >
      {/* Zoom controls */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{
          py: 0.5,
          px: 2,
          flexShrink: 0,
          bgcolor: 'rgba(0,0,0,0.2)',
        }}
      >
        <IconButton
          size="small"
          onClick={() => setPreviewZoom((z) => Math.max(0.25, z - 0.1))}
          sx={{ color: 'rgba(255,255,255,0.8)' }}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            minWidth: 40,
            textAlign: 'center',
            userSelect: 'none',
          }}
        >
          {Math.round(previewZoom * 100)}%
        </Typography>
        <IconButton
          size="small"
          onClick={() => setPreviewZoom((z) => Math.min(2, z + 0.1))}
          sx={{ color: 'rgba(255,255,255,0.8)' }}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Stack>

      {renderError && (
        <Alert severity="error" sx={{ mx: 2, mt: 1, flexShrink: 0 }}>
          {renderError}
        </Alert>
      )}

      {/* Scrollable preview area */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        <Box
          sx={{
            width: `${Math.round(paperWidth * previewZoom)}px`,
            height: `${Math.round(paperHeight * previewZoom)}px`,
            margin: '0 auto',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              width: `${paperWidth}px`,
              minHeight: `${paperHeight}px`,
              transform: `scale(${previewZoom})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0,
              backgroundColor: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <iframe
              ref={iframeRef}
              title="Cutsheet Preview"
              style={{
                width: '100%',
                minHeight: `${paperHeight}px`,
                border: 'none',
                display: 'block',
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const showRestrictedAction = Boolean(restrictedIds);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        height: `calc(100vh - ${marginTop + 120}px)`,
        minHeight: '700px',
      }}
    >
      {/* Controls Sidebar */}
      <Paper
        elevation={2}
        sx={{
          width: isMobile ? '100%' : 320,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={sidebarTab}
          onChange={(_, v) => setSidebarTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Tab label="Settings" />
          <Tab
            label={
              hiddenCount > 0 ? `Visibility (${hiddenCount})` : 'Visibility'
            }
          />
          {isMobile && <Tab label="Preview" />}
        </Tabs>

        {/* ── Tab 0: Print Settings ──────────────────────────────────────── */}
        {sidebarTab === 0 && (
          <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <SettingRow
              settingKey="baseFontSize"
              title={`Base Font Size: ${settings.baseFontSize}px`}
              description="Controls the overall text size throughout the cutsheet"
            >
              <Slider
                value={settings.baseFontSize}
                onChange={(_, value) =>
                  updateSettings({ baseFontSize: value as number })
                }
                min={8}
                max={24}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </SettingRow>

            <SettingRow
              settingKey="primalColumns"
              title="Primal Columns"
              description="Number of columns for displaying primal cuts"
            >
              <ToggleButtonGroup
                value={settings.primalColumns}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) updateSettings({ primalColumns: value });
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value={1}>1</ToggleButton>
                <ToggleButton value={2}>2</ToggleButton>
                <ToggleButton value={3}>3</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              settingKey="specsLayoutPosition"
              title="Specs Layout Position"
              description="Position of specifications, trim, and extras section"
            >
              <ToggleButtonGroup
                value={settings.specsLayoutPosition}
                exclusive
                onChange={(_, value) => {
                  if (value !== null)
                    updateSettings({ specsLayoutPosition: value });
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="top">Top</ToggleButton>
                <ToggleButton value="right">Right</ToggleButton>
                <ToggleButton value="bottom">Bottom</ToggleButton>
                <ToggleButton value="left">Left</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              settingKey="checkboxStyle"
              title="Checkbox Style"
              description="Visual style for selected cut checkboxes"
            >
              <ToggleButtonGroup
                value={settings.checkboxStyle}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) updateSettings({ checkboxStyle: value });
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="check">✓ Check</ToggleButton>
                <ToggleButton value="cross">✕ Cross</ToggleButton>
                <ToggleButton value="fill">■ Fill</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              settingKey="alwaysShowPrimalNotes"
              title="Primal Notes"
              description="Auto hides notes area when empty, Always Show reserves space"
            >
              <ToggleButtonGroup
                value={settings.alwaysShowPrimalNotes}
                exclusive
                onChange={(_, value) => {
                  if (value !== null)
                    updateSettings({ alwaysShowPrimalNotes: value });
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value={false}>Auto</ToggleButton>
                <ToggleButton value>Always Show</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              settingKey="hideUnselectedCuts"
              title="Unselected Cuts"
              description="Show All displays all cuts, Hide removes unselected ones"
            >
              <ToggleButtonGroup
                value={settings.hideUnselectedCuts}
                exclusive
                onChange={(_, value) => {
                  if (value !== null)
                    updateSettings({ hideUnselectedCuts: value });
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value={false}>Show All</ToggleButton>
                <ToggleButton value>Hide Unselected</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>

            <SettingRow
              settingKey="orientation"
              title="Page Orientation"
              description="Portrait is taller, Landscape is wider"
            >
              <ToggleButtonGroup
                value={settings.orientation}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) updateSettings({ orientation: value });
                }}
                size="small"
                fullWidth
              >
                <ToggleButton value="portrait">Portrait</ToggleButton>
                <ToggleButton value="landscape">Landscape</ToggleButton>
              </ToggleButtonGroup>
            </SettingRow>
          </Stack>
        )}

        {/* ── Tab 1: Visibility ──────────────────────────────────────────── */}
        {sidebarTab === 1 && (
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {showRestrictedAction && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityOffIcon fontSize="small" />}
                onClick={handleHideNonAttached}
                sx={{ mb: 2 }}
                fullWidth
              >
                {restrictedActionLabel}
              </Button>
            )}

            {visibilityTree.length === 0 && specificationsList.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ py: 4 }}
              >
                No groups, cuts, or specifications available.
              </Typography>
            ) : (
              <>
                {visibilityTree.length > 0 && (
                  <>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0, mb: 0.5 }}
                    >
                      Cuts & Groups
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Uncheck groups or individual items to hide them from the
                      printed cutsheet
                    </Typography>
                    <List dense disablePadding>
                      {visibilityTree.map((group) => {
                        const groupHidden = settings.hiddenGroupIds.includes(
                          group.id,
                        );
                        const isExpanded = expandedGroups.has(group.id);
                        const hiddenCutCount = groupHidden
                          ? 0
                          : group.cuts.filter((c) =>
                              settings.hiddenItemIds.includes(c.id),
                            ).length;
                        const someHidden =
                          !groupHidden &&
                          hiddenCutCount > 0 &&
                          hiddenCutCount < group.cuts.length;

                        return (
                          <React.Fragment key={group.id}>
                            <ListItemButton
                              dense
                              onClick={() => toggleGroupExpanded(group.id)}
                              sx={{ pl: 0, borderRadius: 1 }}
                            >
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Checkbox
                                  edge="start"
                                  size="small"
                                  checked={!groupHidden}
                                  indeterminate={someHidden}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => toggleGroupHidden(group.id)}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={group.name}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 'medium',
                                  color: groupHidden
                                    ? 'text.disabled'
                                    : 'text.primary',
                                }}
                              />
                              {isExpanded ? (
                                <ExpandLess fontSize="small" />
                              ) : (
                                <ExpandMore fontSize="small" />
                              )}
                            </ListItemButton>

                            <Collapse in={isExpanded} unmountOnExit>
                              <List dense disablePadding>
                                {group.cuts.map((cut) => {
                                  const itemHidden =
                                    settings.hiddenItemIds.includes(cut.id);
                                  return (
                                    <ListItemButton
                                      key={cut.id}
                                      dense
                                      sx={{ pl: 4, borderRadius: 1 }}
                                      onClick={() => toggleItemHidden(cut.id)}
                                      disabled={groupHidden}
                                    >
                                      <ListItemIcon sx={{ minWidth: 32 }}>
                                        <Checkbox
                                          edge="start"
                                          size="small"
                                          checked={!groupHidden && !itemHidden}
                                          disabled={groupHidden}
                                        />
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={cut.name}
                                        primaryTypographyProps={{
                                          variant: 'body2',
                                          color:
                                            groupHidden || itemHidden
                                              ? 'text.disabled'
                                              : 'text.primary',
                                        }}
                                      />
                                      {itemHidden && !groupHidden && (
                                        <VisibilityOffIcon
                                          fontSize="small"
                                          color="disabled"
                                        />
                                      )}
                                    </ListItemButton>
                                  );
                                })}
                              </List>
                            </Collapse>
                          </React.Fragment>
                        );
                      })}
                    </List>
                  </>
                )}

                {specificationsList.length > 0 && (
                  <>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        mt: visibilityTree.length > 0 ? 2 : 0,
                        mb: 0.5,
                      }}
                    >
                      Specifications
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mb: 1, display: 'block' }}
                    >
                      Uncheck specifications to hide them from the printed
                      cutsheet
                    </Typography>
                    <List dense disablePadding>
                      {specificationsList.map((spec) => {
                        const specHidden =
                          settings.hiddenSpecificationIds.includes(spec.id);
                        return (
                          <ListItemButton
                            key={spec.id}
                            dense
                            sx={{ pl: 0, borderRadius: 1 }}
                            onClick={() => toggleSpecificationHidden(spec.id)}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Checkbox
                                edge="start"
                                size="small"
                                checked={!specHidden}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={spec.name}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: specHidden
                                  ? 'text.disabled'
                                  : 'text.primary',
                              }}
                            />
                            {specHidden && (
                              <VisibilityOffIcon
                                fontSize="small"
                                color="disabled"
                              />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </>
                )}
              </>
            )}
          </Box>
        )}

        {/* ── Tab 2 (mobile only): Preview ───────────────────────────────── */}
        {isMobile && sidebarTab === 2 && (
          <Box sx={{ flex: 1, overflow: 'auto' }}>{previewContent}</Box>
        )}

        {/* Save button — pinned to the bottom of the sidebar */}
        {(!hideSaveButton || !hideBlankDownload) && (
          <Box
            sx={{
              p: 2,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Stack spacing={1} direction={'row'}>
              {!hideSaveButton && (
                <Button
                  variant="contained"
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveSettings}
                  disabled={isSaving || !capability?._id}
                  fullWidth
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </Button>
              )}
              {!hideBlankDownload && (
                <Tooltip
                  title={
                    isDirty
                      ? 'Save print settings before generating blank cutsheet'
                      : 'Download Blank Cutsheet'
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleDownloadBlank}
                      disabled={
                        isDownloadingBlank || !capability?._id || isDirty
                      }
                    >
                      {isDownloadingBlank ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Success notification */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSaveSuccess(false)}
          sx={{ width: '100%' }}
        >
          Print settings saved
        </Alert>
      </Snackbar>

      {/* Desktop preview — always visible alongside the sidebar */}
      {!isMobile && previewContent}
    </Box>
  );
}
